import { formatDisplayTitle } from './formatProductTitle';
import { HERO_ACTIONS_DEFAULTS } from './content/heroActionsDefaults';

/** Normalize admin-entered paths for React Router or external links. */
export function normalizeHeroActionPath(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  if (/^(https?:|mailto:|tel:)/i.test(s)) return s;
  return s.startsWith('/') ? s : `/${s}`;
}

export function isExternalHeroActionPath(path) {
  return /^(https?:|mailto:|tel:)/i.test(String(path || '').trim());
}

function coerceHeroBool(val, fallback) {
  if (val === true || val === 1 || val === '1' || val === 'true') return true;
  if (val === false || val === 0 || val === '0' || val === 'false') return false;
  return fallback;
}

export function mergeHeroActions(fromApi) {
  const src = fromApi && typeof fromApi === 'object' ? fromApi : {};
  const out = { ...HERO_ACTIONS_DEFAULTS };
  if (src.fallback === 'rakhi_lumba' || src.fallback === 'sort_order') {
    out.fallback = src.fallback;
  }
  for (const key of Object.keys(HERO_ACTIONS_DEFAULTS)) {
    if (key === 'fallback') continue;
    if (Object.prototype.hasOwnProperty.call(src, key)) {
      out[key] = coerceHeroBool(src[key], out[key]);
    }
  }
  return out;
}

function sortCategories(categories) {
  return [...(categories || [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.slug).localeCompare(String(b.slug)),
  );
}

function fallbackCategories(categories, fallbackMode) {
  if (fallbackMode === 'rakhi_lumba') {
    const rakhi = categories.find((c) => c.slug === 'rakhi');
    const lumba = categories.find((c) => c.slug === 'lumba');
    return [rakhi || null, lumba || null];
  }
  const sorted = sortCategories(categories);
  return [sorted[0] || null, sorted[1] || null];
}

function defaultLabel(which, cat) {
  if (!cat) return which === 1 ? 'Shop collections' : 'Explore more';
  const name = formatDisplayTitle(cat.name || cat.slug);
  return which === 1 ? `Shop ${name}` : `Explore ${name}`;
}

function ctaUsesCategoryFallback(site, which) {
  const labelKey = which === 1 ? 'hero_cta1_label' : 'hero_cta2_label';
  const urlKey = which === 1 ? 'hero_cta1_url' : 'hero_cta2_url';
  return !String(site?.[labelKey] || '').trim() || !normalizeHeroActionPath(site?.[urlKey]);
}

/**
 * True when visible hero buttons need catalog categories (blank admin label or link).
 * @param {'home'|'shop'|'about'} placement
 */
export function heroActionsNeedCatalog(site, placement) {
  const ha = mergeHeroActions(site?.store_copy?.hero_actions);
  const p = placement;
  const needs = (which, showKey) => ha[showKey] !== false && ctaUsesCategoryFallback(site, which);
  return needs(1, `show_primary_${p}`) || needs(2, `show_secondary_${p}`);
}

/**
 * Resolve primary/secondary hero buttons for home, shop, or about.
 * @param {'home'|'shop'|'about'} placement
 * @returns {{ key: string, label: string, path: string, variant: 'primary'|'secondary' }[]}
 */
export function resolveStorefrontHeroActions(site, categories, placement) {
  const ha = mergeHeroActions(site?.store_copy?.hero_actions);
  const [fbPrimary, fbSecondary] = fallbackCategories(categories || [], ha.fallback);

  const buttons = [];

  const add = (which, show) => {
    if (!show) return;
    const labelKey = which === 1 ? 'hero_cta1_label' : 'hero_cta2_label';
    const urlKey = which === 1 ? 'hero_cta1_url' : 'hero_cta2_url';
    const fb = which === 1 ? fbPrimary : fbSecondary;

    let label = String(site?.[labelKey] || '').trim();
    let path = normalizeHeroActionPath(site?.[urlKey]);

    if (!path && fb?.slug) path = `/shop/${encodeURIComponent(fb.slug)}`;
    if (!label) label = defaultLabel(which, fb);
    if (!path) path = '/shop';
    if (!label) return;

    buttons.push({
      key: `cta${which}`,
      label,
      path,
      variant: which === 1 ? 'primary' : 'secondary',
    });
  };

  const p = placement;
  add(1, ha[`show_primary_${p}`] !== false);
  add(2, ha[`show_secondary_${p}`] !== false);

  return buttons;
}

/** @deprecated Use resolveStorefrontHeroActions — kept for tests/imports */
export function resolveHomeHeroCtas(site, rakhiHub, lumbaHub) {
  const categories = [rakhiHub, lumbaHub].filter(Boolean);
  const actions = resolveStorefrontHeroActions(site, categories, 'home');
  return {
    primary: actions[0] || { path: '/shop', label: 'Shop collections' },
    secondary: actions[1] || { path: '/shop', label: 'Explore more' },
  };
}
